// Navvbar.jsx - Updated: server-side lab-scoped search + Communication dropdown
// FIX: permissions now use NUMERIC userId (never ObjectId), resolved from auth user / localStorage,
//      with Authorization header. Admin check is case-insensitive.
// FIX 2: Added Authorization header to search API calls to resolve 401 errors.
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  Navbar,
  Nav,
  Form,
  FormControl,
  Dropdown,
  InputGroup,
  Badge,
} from "react-bootstrap";
import { FaBox, FaMoneyCheckAlt, FaLink, FaSearch, FaBell, FaFileInvoice } from "react-icons/fa";
import { FiMessageSquare, FiAtSign } from "react-icons/fi"; // ADDED
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../login/AuthProvider.js";
import OutsourceUpdateModal from "../OutsourceUpdateModal.js";
import "./Navvbar.css";
import "../../App.css";

// Global upload indicator — shows live upload progress right in the navbar
// so the user can navigate anywhere in the app while files upload.
import NavbarUploadIndicator from "../NavbarUploadIndicator.jsx";

// ADDED: Socket context for real-time chat/notification counts
import { useSocket } from "../../context/SocketContext";

const API_BASE = "https://hms.backend.hiragroupcloud.com";
const SEARCH_DEBOUNCE_MS = 300;

// ============ FIX: NUMERIC USER ID HELPERS ============
// Returns a Number ONLY for pure integers ("15" / 15). ObjectIds are rejected.
const toNumericId = (v) => {
  if (v === undefined || v === null || v === "") return null;
  const s = String(v).trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) ? n : null;
};

const firstNumericId = (candidates) => {
  for (const v of candidates) {
    const n = toNumericId(v);
    if (n !== null) return n;
  }
  return null;
};

const resolveNumericUserId = (authUser) => {
  // 1. From auth user (numeric userId only)
  if (authUser) {
    const n = firstNumericId([
      authUser.userId,
      authUser.userid,
      authUser?.data?.userId,
      authUser?.user?.userId,
    ]);
    if (n !== null) return n;
  }

  // 2. Direct localStorage key
  try {
    const n = toNumericId(localStorage.getItem("userId"));
    if (n !== null) return n;
  } catch (_) {}

  // 3. JSON stores in localStorage
  const keys = ["userData", "userProfile", "user", "auth", "currentUser", "labUser", "labUserData"];
  for (const key of keys) {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) continue;
      const parsed = JSON.parse(stored);
      const n = firstNumericId([
        parsed?.userId,
        parsed?.userid,
        parsed?.data?.userId,
        parsed?.user?.userId,
      ]);
      if (n !== null) return n;
    } catch (_) {}
  }
  return null;
};

const isAdminRoleName = (role) => {
  if (!role) return false;
  const r = String(role).trim().toLowerCase();
  return r === "admin" || r === "superadmin" || r === "super-admin" || r === "super_admin";
};

function Navvbar() {
  const [selectedOption, setSelectedOption] = useState("Cases");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permittedPaths, setPermittedPaths] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  const [userRole, setUserRole] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();

  // FIX: derived values (numeric id + admin flag)
  const numericUserId = resolveNumericUserId(user);
  const isAdminUser = isAdminRoleName(userRole);

  // ADDED: Real-time unread counts from socket
  const { unreadCount, chatUnreadByGroup } = useSocket();
  const totalChatUnread = Object.values(chatUnreadByGroup || {}).reduce(
    (a, b) => a + (b || 0),
    0
  );

  // State for lab user info
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);

  // Outsource notification states
  const [hasOutsourceUpdates, setHasOutsourceUpdates] = useState(false);
  const [outsourceUpdates, setOutsourceUpdates] = useState({
    toOutsource: [],
    received: [],
    hasUpdates: false
  });
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [notificationDot, setNotificationDot] = useState({
    toOutsource: false,
    received: false
  });

  const searchDebounceRef = useRef(null);
  const searchAbortControllerRef = useRef(null);
  const searchSeqRef = useRef(0);
  const lastResultsRef = useRef([]);

  const [scaleFactor, setScaleFactor] = useState(1);

  useEffect(() => {
    const calculateScale = () => {
      const width = window.innerWidth;
      let scale = Math.min(1, width / 1440);
      scale = Math.max(0.45, scale);
      setScaleFactor(scale);
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  const scaled = (size) => Math.round(size * scaleFactor);

  const formatLabId = (id) => {
    if (!id) return '00000000';
    const idStr = String(id);
    if (/^\d+$/.test(idStr)) {
      return idStr.padStart(8, '0');
    }
    return idStr;
  };

  useEffect(() => {
    let storedLabId = localStorage.getItem('userLabId');
    let storedUserType = localStorage.getItem('userType');

    if (!storedLabId) {
      storedLabId = sessionStorage.getItem('userLabId');
    }

    if (!storedUserType) {
      storedUserType = sessionStorage.getItem('userType');
    }

    const userDataStr = localStorage.getItem('userData') || sessionStorage.getItem('userData');
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);

        if (!storedLabId && userData.labId) {
          storedLabId = userData.labId;
        }

        if (!storedUserType && userData.userType) {
          storedUserType = userData.userType;
        }
      } catch (e) {
        // Handle error silently
      }
    }

    if (storedLabId) {
      const formattedLabId = formatLabId(storedLabId);
      setLabId(formattedLabId);
    }

    if (storedUserType) {
      setUserType(storedUserType);
    } else if (storedLabId) {
      setUserType('lab');
    } else {
      setUserType('admin');
    }

    const savedLastChecked = localStorage.getItem('outsourceLastChecked');
    if (savedLastChecked) {
      setLastChecked(savedLastChecked);
    }

    setAuthLoaded(true);
  }, []);

  // FIX: role is synced from localStorage AND from auth user (re-runs when user changes)
  useEffect(() => {
    const storedUserRole = localStorage.getItem("userRole");
    const fromUser =
      user?.userRole ?? user?.role ?? user?.data?.userRole ?? user?.data?.role ?? null;
    const resolved = storedUserRole || fromUser;
    if (resolved) {
      setUserRole(String(resolved).trim());
    }
  }, [user]);

  const checkForOutsourceUpdates = useCallback(async () => {
    if (!labId) return;

    try {
      const token = localStorage.getItem("accessToken");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(
        `${API_BASE}/api/outsource-case/updates/${labId}`,
        {
          params: {
            lastChecked: lastChecked || undefined
          },
          headers // <-- Added Authorization Header to fix 401
        }
      );

      if (response.data && response.data.success) {
        const updates = response.data.data;

        if (updates.hasUpdates) {
          setHasOutsourceUpdates(true);
          setOutsourceUpdates(updates);

          setNotificationDot({
            toOutsource: updates.toOutsource?.length > 0,
            received: updates.received?.length > 0
          });
        }
      }
    } catch (error) {
      // Handle error silently
    }
  }, [labId, lastChecked]);

  useEffect(() => {
    if (!authLoaded || !labId) return;

    checkForOutsourceUpdates();

    const interval = setInterval(checkForOutsourceUpdates, 30000);

    return () => clearInterval(interval);
  }, [authLoaded, labId, checkForOutsourceUpdates]);

  const handleNotificationClick = () => {
    setShowUpdateModal(true);
  };

  const handleCloseModal = () => {
    setShowUpdateModal(false);

    const now = new Date().toISOString();
    setLastChecked(now);
    localStorage.setItem('outsourceLastChecked', now);

    setNotificationDot({
      toOutsource: false,
      received: false
    });
    setHasOutsourceUpdates(false);
  };

  // FIX: permissions fetch uses NUMERIC userId + Authorization header + cancel flag
  useEffect(() => {
    // Nothing to work with yet (auth still hydrating)
    if (!user && !userRole) return;

    let cancelled = false;

    const loadPermittedPages = async () => {
      if (isAdminUser) {
        const adminPaths = [
          "/home", "/partner-labs",
          "/overview", "/accountDetails", "/newPlanEstimate", "/newCasePickup", "/casesOpened",
          "/casesArrive", "/caseship", "/creatShipment", "/openTryInCases", "/casesOnHold",
          "/toOutsource", "/outsource-received", "/caseEstimates", "/openCases", "/undelivarableCases",
          "/financeOwerview", "/posting", "/statement",
          "/outsource-finances-overview", "/outsource-finances-postings", "/outsource-finances-statements",
          "/reports",
          "/promtoionOverview", "/website", "/ddxAdd", "/coupon", "/action", "/widgets", "/ddxAccount",
          "/Tabs", "/store", "/users", "/PermissionGroups", "/accountTable",
          "/caseSetting"
        ];

        if (cancelled) return;
        setPermittedPaths(adminPaths);
        setIsLoadingPermissions(false);
        return;
      }

      // No numeric userId resolvable → stop spinner, show nothing (no ObjectId fallback)
      if (numericUserId === null) {
        if (cancelled) return;
        setPermittedPaths([]);
        setIsLoadingPermissions(false);
        return;
      }

      try {
        const token = localStorage.getItem("accessToken");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const response = await axios.get(
          `${API_BASE}/api/page/user/${numericUserId}/pages`,
          { headers }
        );
        const data = response.data;

        if (cancelled) return;

        if (Array.isArray(data)) {
          const paths = data
            .filter(permission => permission && permission.page && permission.isVisible)
            .map(permission => permission.page.path)
            .filter(path => path);

          setPermittedPaths(paths);
        } else {
          setPermittedPaths([]);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("[Navvbar] Failed to load permitted pages:", error?.message || error);
        setPermittedPaths([]);
      }
      if (!cancelled) setIsLoadingPermissions(false);
    };

    loadPermittedPages();

    return () => {
      cancelled = true;
    };
  }, [user, userRole, numericUserId, isAdminUser]);

  const runSearch = useCallback(async (term, option, { silent = false } = {}) => {
    const isAdmin = userType !== 'lab';

    if (!isAdmin && !labId) {
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
        searchAbortControllerRef.current = null;
      }
      searchSeqRef.current += 1;
      setSearchResults([]);
      lastResultsRef.current = [];
      setShowResults(true);
      return;
    }

    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    searchAbortControllerRef.current = controller;

    const seq = ++searchSeqRef.current;

    if (!silent) setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("accessToken");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(`${API_BASE}/api/search/navbar`, {
        params: {
          type: option,
          term,
          labId: isAdmin ? undefined : labId,
          isAdmin,
        },
        headers, // <-- ADDED AUTHORIZATION HEADER
        signal: controller.signal,
      });

      if (seq !== searchSeqRef.current) return;

      if (response.data && response.data.success) {
        const results = response.data.data || [];
        setSearchResults(results);
        lastResultsRef.current = results;
      } else {
        setSearchResults([]);
        lastResultsRef.current = [];
      }
      setShowResults(true);
    } catch (err) {
      if (axios.isCancel(err) || err.code === "ERR_CANCELED" || err.name === "CanceledError") {
        return;
      }
      if (seq !== searchSeqRef.current) return;
      setError("Unable to search right now.");
      setSearchResults([]);
      lastResultsRef.current = [];
      setShowResults(true);
    } finally {
      if (seq === searchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [labId, userType]);

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    const trimmed = term.trim();
    if (!trimmed) {
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
        searchAbortControllerRef.current = null;
      }
      searchSeqRef.current += 1;
      setShowResults(false);
      setSearchResults([]);
      lastResultsRef.current = [];
      setLoading(false);
      setError(null);
      return;
    }

    if (lastResultsRef.current.length > 0) {
      setShowResults(true);
    }

    searchDebounceRef.current = setTimeout(() => {
      runSearch(trimmed, selectedOption, {
        silent: lastResultsRef.current.length > 0,
      });
    }, SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed) {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      lastResultsRef.current = [];
      setSearchResults([]);
      setLoading(true);
      runSearch(trimmed, selectedOption, { silent: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  const handleKeyPress = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    const trimmed = searchTerm.trim();
    if (!trimmed) return;

    const isAdmin = userType !== 'lab';
    if (!isAdmin && !labId) return;

    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    searchAbortControllerRef.current = controller;
    const seq = ++searchSeqRef.current;

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("accessToken");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(`${API_BASE}/api/search/navbar`, {
        params: {
          type: selectedOption,
          term: trimmed,
          labId: isAdmin ? undefined : labId,
          isAdmin,
        },
        headers, // <-- ADDED AUTHORIZATION HEADER
        signal: controller.signal,
      });

      if (seq !== searchSeqRef.current) return;

      const results = (response.data && response.data.success) ? (response.data.data || []) : [];
      setSearchResults(results);
      lastResultsRef.current = results;
      setShowResults(true);

      if (results.length > 0) {
        handleResultClick(results[0]);
      }
    } catch (err) {
      if (axios.isCancel(err) || err.code === "ERR_CANCELED" || err.name === "CanceledError") {
        return;
      }
      if (seq !== searchSeqRef.current) return;
      setError("Unable to search right now.");
      setSearchResults([]);
      lastResultsRef.current = [];
      setShowResults(true);
    } finally {
      if (seq === searchSeqRef.current) {
        setLoading(false);
      }
    }
  };

  const handleAccountClick = (account) => {
    navigate(`/TabsWorking/${account.accountId}`, {
      state: {
        accountName: account.name,
        accountData: account,
      },
    });
    setShowResults(false);
    setSearchTerm("");
    lastResultsRef.current = [];
  };

  const handleCaseClick = (caseId) => {
    window.location.href = `/case/${caseId}`;
    setShowResults(false);
    setSearchTerm("");
    lastResultsRef.current = [];
  };

  const handleInvoiceResultClick = (invoice) => {
    if (invoice.caseId) {
      window.location.href = `/case/${invoice.caseId}`;
    } else {
      window.location.href = `/statement`;
    }
    setShowResults(false);
    setSearchTerm("");
    lastResultsRef.current = [];
  };

  const handleInvoiceClick = (invoice) => {
    if (invoice.caseId) {
      window.location.href = `/case/${invoice.caseId}`;
    } else {
      window.location.href = `/statement`;
    }
    setShowResults(false);
    setSearchTerm("");
    lastResultsRef.current = [];
  };

  const handleOutsourceCaseClick = (outsourceCase) => {
    if (outsourceCase.caseNumber) {
      navigate(`/outsource-case/${outsourceCase.caseNumber}`);
    }
    setShowResults(false);
    setSearchTerm("");
    lastResultsRef.current = [];
  };

  const handleResultClick = (result) => {
    if (selectedOption === "Accounts") {
      handleAccountClick(result);
    } else if (selectedOption === "Cases") {
      if (result.isInvoiceResult) {
        handleInvoiceResultClick(result);
      } else {
        handleCaseClick(result.case_id);
      }
    } else if (selectedOption === "Invoices") {
      handleInvoiceClick(result);
    } else if (selectedOption === "Outsourced") {
      handleOutsourceCaseClick(result);
    }
  };

  const allDropdownItems = {
    dashboard: [
      { path: "/home", name: "Overview" },
      { path: "/partner-labs", name: "Partner Labs" }
    ],
    cases: [
      { path: "/overview", name: "Overview" },
      { path: "/accountDetails", name: "New Case" },
      { path: "/newPlanEstimate", name: "New Case Plan for Estimate" },
      { path: "/newCasePickup", name: "New Case Pickup" },
      { path: "/casesOpened", name: "By Month" },
      { path: "/casesArrive", name: "To Arrive" },
      { path: "/caseship", name: "To Ship" },
      { path: "/creatShipment", name: "Create Shipments" },
      { path: "/openTryInCases", name: "Open Try-Ins" },
      { path: "/casesOnHold", name: "On Hold" },
      { path: "/toOutsource", name: "To Outsource" },
      { path: "/outsource-received", name: "Outsource Received" },
      { path: "/caseEstimates", name: "Case Plans / Estimates" },
      { path: "/openCases", name: "Open Cases" },
      { path: "/undelivarableCases", name: "Undeliverable Cases" }
    ],
    finances: [
      { path: "/financeOwerview", name: "Overview" },
      { path: "/posting", name: "Postings" },
      { path: "/statement", name: "Statements" }
    ],
    promotions: [
      { path: "/promtoionOverview", name: "Overview" },
      { path: "/website", name: "Websites" },
      { path: "/ddxAdd", name: "Ads" },
      { path: "/coupon", name: "Coupons" },
      { path: "/action", name: "Links" },
      { path: "/widgets", name: "Widgets" },
      { path: "/ddxAccount", name: "DDX Account Onboarding" }
    ],
    settings: [
      { path: "/Tabs", name: "Lab Details" },
      { path: "/store", name: "Store" },
      { path: "/users", name: "Users" },
      { path: "/PermissionGroups", name: "Permission Group" },
      { path: "/accountTable", name: "Accounts" },
      { path: "/financeOwerview", name: "Finances" },
      { path: "/caseSetting", name: "Case Settings" }
    ],
    outsourceFinances: [
      { path: "/outsource-finances-overview", name: "Overview" },
      { path: "/outsource-postings", name: "Postings" },
      { path: "/outsource-finances-statements", name: "Statements" }
    ]
  };

  const disabledPaths = [
    "/casesArrive",
    "/newPlanEstimate",
    "/newCasePickup",
    "/creatShipment",
    "/openTryInCases",
    "/caseEstimates",
    "/undelivarableCases",
    "/Tabs",
    "/store",
    "/PermissionGroups"
  ];

  const visibleButDisabledPaths = [
    "/outsource-finances-overview",
    "/outsource-finances-statements"
  ];

  // FIX: case-insensitive path matching (e.g. "/Tabs" vs "/tabs")
  const permittedPathsLower = permittedPaths.map((p) => String(p).toLowerCase());
  const isPathPermitted = (p) => permittedPathsLower.includes(String(p).toLowerCase());

  const getFilteredItems = (menu) => {
    if (isLoadingPermissions) {
      return [{ path: "loading", name: "Loading permissions..." }];
    }

    if (isAdminUser) {
      return allDropdownItems[menu] || [];
    }

    const items = (allDropdownItems[menu] || []).filter(item =>
      isPathPermitted(item.path)
    );

    return items.length > 0 ? items : [];
  };

  const isReportsPermitted = !isLoadingPermissions && (
    isAdminUser || isPathPermitted("/reports")
  );

  const isDashboardPermitted = !isLoadingPermissions && (
    isAdminUser || getFilteredItems("dashboard").length > 0
  );

  const isCasesPermitted = !isLoadingPermissions && (
    isAdminUser || getFilteredItems("cases").length > 0
  );

  const isFinancesPermitted = !isLoadingPermissions && (
    isAdminUser || getFilteredItems("finances").length > 0
  );

  const isSettingsPermitted = !isLoadingPermissions && (
    isAdminUser || getFilteredItems("settings").length > 0
  );

  const isOutsourceFinancesPermitted = !isLoadingPermissions && (
    isAdminUser || getFilteredItems("outsourceFinances").length > 0
  );

  const showRoleBadge = userRole && !isAdminUser;
  const showLabBadge = userType === 'lab' && labId;

  const isPathDisabled = (path) => {
    return disabledPaths.includes(path) || visibleButDisabledPaths.includes(path);
  };

  const getOutsourceLabel = (outsourceCase) => {
    if (!labId) return 'Associated with';

    const sourceLabId = outsourceCase.sourceLab?.userLabId || outsourceCase.sourceLab?.labId;
    const destLabId = outsourceCase.destinationLab?.userLabId || outsourceCase.destinationLab?.labId;

    if (sourceLabId === labId) {
      return 'Sent to';
    } else if (destLabId === labId) {
      return 'Received from';
    }
    return 'Associated with';
  };

  const getPartnerLabName = (outsourceCase) => {
    if (!labId) return 'Unknown Lab';

    const sourceLabId = outsourceCase.sourceLab?.userLabId || outsourceCase.sourceLab?.labId;
    const destLabId = outsourceCase.destinationLab?.userLabId || outsourceCase.destinationLab?.labId;

    const getLabName = (lab) => {
      if (!lab) return 'Unknown Lab';
      return lab.labName || lab.name || 'Unknown Lab';
    };

    if (sourceLabId === labId) {
      return getLabName(outsourceCase.destinationLab);
    } else if (destLabId === labId) {
      return getLabName(outsourceCase.sourceLab);
    }

    const sourceName = getLabName(outsourceCase.sourceLab);
    const destName = getLabName(outsourceCase.destinationLab);

    if (sourceName !== 'Unknown Lab' && destName !== 'Unknown Lab') {
      return `${sourceName} → ${destName}`;
    }

    return sourceName !== 'Unknown Lab' ? sourceName :
           destName !== 'Unknown Lab' ? destName : 'Unknown Lab';
  };

  const navToggleStyle = {
    fontSize: `${scaled(14)}px`,
    padding: `${scaled(6)}px ${scaled(12)}px`,
  };

  const navMenuItemStyle = {
    fontSize: `${scaled(13)}px`,
  };

  const blurTimeoutRef = useRef(null);
  const handleSearchBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setShowResults(false);
    }, 200);
  };
  const handleSearchFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    if (searchTerm.trim().length > 0) {
      setShowResults(true);
    }
  };

  return (
    <div
      className="navvbar-container"
      style={{
        padding: `${scaled(18)}px ${scaled(22)}px`,
      }}
    >
      <NavbarUploadIndicator />

      {showRoleBadge && (
        <div
          className="navvbar-role-badge"
          style={{
            padding: `${scaled(2)}px ${scaled(10)}px`,
            fontSize: `${scaled(12)}px`,
          }}
        >
          {userRole}
        </div>
      )}

      {hasOutsourceUpdates && (
        <div className="navvbar-notification-wrapper">
          <button
            onClick={handleNotificationClick}
            className="navvbar-notification-btn"
            style={{
              width: `${scaled(36)}px`,
              height: `${scaled(36)}px`,
            }}
            title="New outsource case updates!"
          >
            <FaBell
              className="navvbar-notification-icon"
              style={{ fontSize: `${scaled(18)}px` }}
            />
            <span
              className="navvbar-notification-count"
              style={{
                width: `${scaled(14)}px`,
                height: `${scaled(14)}px`,
              }}
            >
              <span style={{ fontSize: `${scaled(8)}px` }}>
                {outsourceUpdates.totalCaseUpdates || '!'}
              </span>
            </span>
          </button>
        </div>
      )}

      <Nav
        className="navvbar-nav"
        style={{
          rowGap: `${scaled(8)}px`,
          columnGap: `${scaled(6)}px`,
        }}
      >
        {isDashboardPermitted && (
          <Dropdown>
            <Dropdown.Toggle
              variant="default"
              className="white-button"
              disabled={isLoadingPermissions}
              style={navToggleStyle}
            >
              Dashboard
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {getFilteredItems("dashboard").map((item) => (
                <Dropdown.Item
                  key={item.path}
                  as={Link}
                  to={item.path}
                  style={navMenuItemStyle}
                >
                  {item.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}

        {isCasesPermitted && (
          <Dropdown>
            <Dropdown.Toggle
              variant="default"
              className="white-button"
              disabled={isLoadingPermissions}
              style={{ ...navToggleStyle, position: 'relative' }}
            >
              Cases
              {hasOutsourceUpdates && (
                <span
                  style={{
                    position: "absolute",
                    top: "2px",
                    right: "2px",
                    backgroundColor: "#dc3545",
                    borderRadius: "50%",
                    width: `${scaled(10)}px`,
                    height: `${scaled(10)}px`,
                    display: "inline-block"
                  }}
                ></span>
              )}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {getFilteredItems("cases").map((item) => {
                const isDisabled = isPathDisabled(item.path);
                const isToOutsource = item.path === "/toOutsource";
                const isReceived = item.path === "/outsource-received";

                return (
                  <Dropdown.Item
                    key={item.path}
                    as={isDisabled ? "div" : Link}
                    to={isDisabled ? undefined : item.path}
                    className={isDisabled ? "disabled-item" : ""}
                    style={{
                      ...navMenuItemStyle,
                      color: isDisabled ? "#999" : "inherit",
                      fontWeight: isDisabled ? "bold" : "normal",
                    }}
                  >
                    <span>{item.name}</span>
                    {isToOutsource && notificationDot.toOutsource && (
                      <span className="navvbar-notification-dot"></span>
                    )}
                    {isReceived && notificationDot.received && (
                      <span className="navvbar-notification-dot"></span>
                    )}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        )}

        {isFinancesPermitted && (
          <Dropdown>
            <Dropdown.Toggle
              variant="transparent"
              className="white-button"
              disabled={isLoadingPermissions}
              style={navToggleStyle}
            >
              Finances
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {getFilteredItems("finances").map((item) => (
                <Dropdown.Item key={item.path} as={Link} to={item.path} style={navMenuItemStyle}>
                  {item.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}

        {isReportsPermitted && (
          <Nav.Item>
            <Link
              to="/reports"
              className="nav-link"
              style={{ fontSize: `${scaled(14)}px` }}
            >
              Reports
            </Link>
          </Nav.Item>
        )}

        {isSettingsPermitted && (
          <Dropdown>
            <Dropdown.Toggle
              variant="transparent"
              className="white-button"
              disabled={isLoadingPermissions}
              style={navToggleStyle}
            >
              Settings
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {getFilteredItems("settings").map((item) => {
                const isDisabled = isPathDisabled(item.path);
                return (
                  <Dropdown.Item
                    key={item.path}
                    as={isDisabled ? "div" : Link}
                    to={isDisabled ? undefined : item.path}
                    className={isDisabled ? "disabled-item" : ""}
                    style={{
                      ...navMenuItemStyle,
                      color: isDisabled ? "#999" : "inherit",
                      fontWeight: isDisabled ? "bold" : "normal",
                    }}
                  >
                    {item.name}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        )}

        {isOutsourceFinancesPermitted && (
          <Dropdown>
            <Dropdown.Toggle
              variant="transparent"
              className="white-button"
              disabled={isLoadingPermissions}
              style={navToggleStyle}
            >
              Outsource Finances
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {getFilteredItems("outsourceFinances").map((item) => {
                const isDisabled = isPathDisabled(item.path);
                return (
                  <Dropdown.Item
                    key={item.path}
                    as={isDisabled ? "div" : Link}
                    to={isDisabled ? undefined : item.path}
                    className={isDisabled ? "disabled-item" : ""}
                    style={{
                      ...navMenuItemStyle,
                      color: isDisabled ? "#999" : "inherit",
                      fontWeight: isDisabled ? "bold" : "normal",
                    }}
                  >
                    <span>{item.name}</span>
                    {isDisabled && visibleButDisabledPaths.includes(item.path) && (
                      <span className="navvbar-coming-soon">
                        (Coming Soon)
                      </span>
                    )}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        )}

        {/* ADDED: Communication dropdown */}
        <Dropdown>
          <Dropdown.Toggle
            variant="transparent"
            className="white-button"
            disabled={isLoadingPermissions}
            style={{ ...navToggleStyle, position: 'relative' }}
          >
            Communication
            {unreadCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  background: '#dc3545',
                  color: 'white',
                  borderRadius: 8,
                  fontSize: 10,
                  padding: '0 5px',
                  minWidth: 14,
                  textAlign: 'center'
                }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item as={Link} to="/chat" style={navMenuItemStyle}>
              <FiMessageSquare className="me-2" />
              Chat Groups
              {totalChatUnread > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    background: '#dc3545',
                    color: 'white',
                    borderRadius: 8,
                    fontSize: 10,
                    padding: '0 5px'
                  }}
                >
                  {totalChatUnread > 99 ? '99+' : totalChatUnread}
                </span>
              )}
            </Dropdown.Item>
            <Dropdown.Item as={Link} to="/mentions" style={navMenuItemStyle}>
              <FiAtSign className="me-2" />
              My Mentions
            </Dropdown.Item>
            <Dropdown.Item as={Link} to="/notifications" style={navMenuItemStyle}>
              <FaBell className="me-2" />
              Notifications
              {unreadCount > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    background: '#dc3545',
                    color: 'white',
                    borderRadius: 8,
                    fontSize: 10,
                    padding: '0 5px'
                  }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
        {/* END: Communication dropdown */}

        {!isLoadingPermissions &&
         !isAdminUser &&
         !isDashboardPermitted &&
         !isCasesPermitted &&
         !isFinancesPermitted &&
         !isReportsPermitted &&
         !isSettingsPermitted &&
         !isOutsourceFinancesPermitted && (
          <Nav.Item>
            <span
              className="nav-link navvbar-no-permissions"
              style={{ fontSize: `${scaled(14)}px` }}
            >
              No menu permissions assigned
            </span>
          </Nav.Item>
        )}

        <div
          className="navvbar-search-wrapper"
          style={{
            flex: `1 1 ${scaled(200)}px`,
            minWidth: `${scaled(180)}px`,
            maxWidth: `${scaled(300)}px`,
          }}
        >
          <Form className="d-flex search-bar">
            <InputGroup>
              <FormControl
                type="search"
                className="search-input"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={handleKeyPress}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                style={{ fontSize: `${scaled(13)}px` }}
              />
              <Dropdown align="end" drop="down">
                <Dropdown.Toggle
                  style={{
                    fontSize: `${scaled(13)}px`,
                    padding: `${scaled(6)}px ${scaled(10)}px`,
                  }}
                >
                  {selectedOption || "Select"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setSelectedOption("Cases")} style={navMenuItemStyle}>
                    <FaBox className="me-2" /> Cases {userType === 'lab' ? '(Lab)' : ''}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setSelectedOption("Accounts")} style={navMenuItemStyle}>
                    <FaMoneyCheckAlt className="me-2" /> Accounts {userType === 'lab' ? '(Lab)' : ''}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setSelectedOption("Invoices")} style={navMenuItemStyle}>
                    <FaFileInvoice className="me-2" /> Invoices
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => setSelectedOption("Outsourced")}
                    style={navMenuItemStyle}
                  >
                    <FaLink className="me-2" /> Outsourced
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </InputGroup>
          </Form>

          {showResults && (
            <div
              className="navvbar-search-results"
              style={{ fontSize: `${scaled(14)}px` }}
            >
              {loading && searchResults.length === 0 ? (
                <div className="navvbar-search-loading">Loading...</div>
              ) : error ? (
                <div className="navvbar-search-error">{error}</div>
              ) : searchResults.length === 0 ? (
                <div className="navvbar-search-empty">
                  {userType === 'lab'
                    ? `No ${selectedOption.toLowerCase()} found for your lab`
                    : `No ${selectedOption.toLowerCase()} found`}
                </div>
              ) : (
                <>
                  {loading && (
                    <div
                      className="navvbar-search-loading"
                      style={{
                        padding: "4px 10px",
                        fontSize: "11px",
                        opacity: 0.65,
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      Updating…
                    </div>
                  )}
                  {searchResults.map((result) => (
                    <div
                      key={result._id}
                      onClick={() => handleResultClick(result)}
                      className="navvbar-search-result-item"
                    >
                      {result.isOutsourceResult ? (
                        <div>
                          <div style={{ marginBottom: "4px", display: "flex", alignItems: "center" }}>
                            <FaLink style={{ color: "#004aad", marginRight: "8px" }} />
                            <strong>Case Number:</strong> {result.caseNumber || "N/A"}
                          </div>
                          <div style={{ marginBottom: "4px" }}>
                            <strong>{getOutsourceLabel(result)}:</strong>{" "}
                            <span style={{ fontWeight: "500" }}>
                              {getPartnerLabName(result)}
                            </span>
                          </div>
                          {result.sourceLabName && result.destinationLabName &&
                           result.sourceLabName !== 'Unknown Lab' &&
                           result.destinationLabName !== 'Unknown Lab' && (
                            <div style={{ marginBottom: "4px", fontSize: "12px", color: "#666" }}>
                              <span>Source: {result.sourceLabName}</span>
                              <span style={{ margin: "0 8px" }}>→</span>
                              <span>Destination: {result.destinationLabName}</span>
                            </div>
                          )}
                          {result.patientName && (
                            <div style={{ marginBottom: "4px" }}>
                              <strong>Patient:</strong> {result.patientName}
                            </div>
                          )}
                          {result.status && (
                            <div style={{ marginBottom: "4px" }}>
                              <strong>Status:</strong>{" "}
                              <span style={{
                                color: result.status.toLowerCase() === 'completed' ? 'green' :
                                       result.status.toLowerCase() === 'in-progress' ? 'orange' :
                                       result.status.toLowerCase() === 'pending' ? '#ffc107' : 'inherit',
                                fontWeight: 'bold'
                              }}>
                                {result.status}
                              </span>
                            </div>
                          )}
                          {result.createdAt && (
                            <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                              <strong>Created:</strong> {new Date(result.createdAt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ) : result.isInvoiceResult ? (
                        <div>
                          <div style={{ marginBottom: "4px", display: "flex", alignItems: "center" }}>
                            <FaFileInvoice style={{ color: "#004aad", marginRight: "8px" }} />
                            <strong>Invoice:</strong> {result.invoiceNumber || result.invoiceId || "N/A"}
                          </div>
                          {result.caseId && (
                            <div style={{ marginBottom: "4px" }}>
                              <strong>Case ID:</strong> {result.caseId}
                            </div>
                          )}
                          {result.patientName && (
                            <div style={{ marginBottom: "4px" }}>
                              <strong>Patient:</strong> {result.patientName}
                            </div>
                          )}
                          {result.accountName && (
                            <div style={{ marginBottom: "4px" }}>
                              <strong>Account:</strong> {result.accountName}
                            </div>
                          )}
                          {result.totalAmount !== undefined && (
                            <div style={{ marginBottom: "4px" }}>
                              <strong>Amount:</strong> ${typeof result.totalAmount === 'number' ? result.totalAmount.toFixed(2) : result.totalAmount}
                            </div>
                          )}
                        </div>
                      ) : selectedOption === "Accounts" ? (
                        <div>
                          <div>
                            <strong>{result.name}</strong>
                          </div>
                          <div>Account ID: {result.accountId}</div>
                          {result.source && <div>Type: {result.source}</div>}
                        </div>
                      ) : selectedOption === "Invoices" ? (
                        <div>
                          <div style={{ marginBottom: "4px" }}>
                            <strong>Invoice #:</strong> {result.invoiceNumber || result.invoiceId || "N/A"}
                          </div>
                          {result.caseId && (
                            <div style={{ marginBottom: "4px" }}>
                              <strong>Case ID:</strong> {result.caseId}
                            </div>
                          )}
                          {result.patientName && (
                            <div style={{ marginBottom: "4px" }}>
                              <strong>Patient:</strong> {result.patientName}
                            </div>
                          )}
                          {result.accountName && (
                            <div style={{ marginBottom: "4px" }}>
                              <strong>Account:</strong> {result.accountName}
                            </div>
                          )}
                          {result.totalAmount !== undefined && (
                            <div style={{ marginBottom: "4px" }}>
                              <strong>Amount:</strong> ${typeof result.totalAmount === 'number' ? result.totalAmount.toFixed(2) : result.totalAmount}
                            </div>
                          )}
                          {result.status && (
                            <div>
                              <strong>Status:</strong>{" "}
                              <span style={{
                                color: result.status.toLowerCase() === 'paid' ? 'green' :
                                       result.status.toLowerCase() === 'pending' ? 'orange' : 'inherit'
                              }}>
                                {result.status}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div style={{ marginBottom: "4px" }}>
                            <strong>Case ID:</strong> {result.case_id}
                          </div>
                          <div style={{ marginBottom: "4px" }}>
                            <strong>Patient:</strong> {result.name || "N/A"}
                          </div>
                          <div>
                            <strong>Account:</strong> {result.account_name || "N/A"} {result.account_id && result.account_id !== "N/A" ? `(${result.account_id})` : ""}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </Nav>

      <OutsourceUpdateModal
        show={showUpdateModal}
        onHide={handleCloseModal}
        updates={outsourceUpdates}
        userLabId={labId}
      />
    </div>
  );
}

export default Navvbar;